<template>
  <div style="max-width: 720px; margin: 0 auto; padding: 48px 20px">
    <!-- Success state -->
    <div v-if="submitted" style="text-align: center; padding: 40px 20px">
      <div style="font-size: 48px; margin-bottom: 16px">✓</div>
      <h1
        style="
          font-family: var(--display);
          font-size: 28px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin-bottom: 12px;
        "
      >
        Suggestion envoyée !
      </h1>
      <p style="color: var(--ink-dim); margin-bottom: 32px">
        Votre suggestion sera vérifiée par un administrateur avant d'être
        publiée.
      </p>
      <nuxt-link to="/" class="btn btn-primary"
        >← Retour aux événements</nuxt-link
      >
    </div>

    <template v-else>
      <!-- Title -->
      <div style="margin-bottom: 40px">
        <div class="hero-kicker">
          <div class="hero-kicker-dot" />
          Formulaire
        </div>
        <h1 class="hero-title" style="font-size: clamp(28px, 4vw, 52px)">
          Suggérer un<br /><em>événement.</em>
        </h1>
        <p style="color: var(--ink-dim); font-size: 13px">
          Proposez un événement CrossFit. Il sera vérifié par un administrateur
          avant d'être publié.
        </p>
      </div>

      <form
        style="display: flex; flex-direction: column; gap: 32px"
        @submit.prevent="handleSubmit"
      >
        <!-- Contact section -->
        <div>
          <h2
            style="
              font-family: var(--mono);
              font-size: 10px;
              letter-spacing: 0.18em;
              text-transform: uppercase;
              color: var(--ink-dim);
              margin-bottom: 20px;
              padding-bottom: 8px;
              border-bottom: 1px solid var(--line);
            "
          >
            Informations de contact
          </h2>
          <div
            style="
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 24px 32px;
            "
          >
            <div>
              <label for="firstName" class="field-label">Prénom *</label>
              <input
                id="firstName"
                v-model="form.firstName"
                class="field-input"
                :class="{ invalid: errors.firstName }"
                type="text"
                placeholder="Votre prénom"
              />
              <span v-if="errors.firstName" class="field-error">{{
                errors.firstName
              }}</span>
            </div>
            <div>
              <label for="lastName" class="field-label">Nom *</label>
              <input
                id="lastName"
                v-model="form.lastName"
                class="field-input"
                :class="{ invalid: errors.lastName }"
                type="text"
                placeholder="Votre nom"
              />
              <span v-if="errors.lastName" class="field-error">{{
                errors.lastName
              }}</span>
            </div>
            <div>
              <label for="email" class="field-label">Email *</label>
              <input
                id="email"
                v-model="form.email"
                class="field-input"
                :class="{ invalid: errors.email }"
                type="email"
                placeholder="votre.email@exemple.com"
              />
              <span v-if="errors.email" class="field-error">{{
                errors.email
              }}</span>
            </div>
            <div>
              <label for="phone" class="field-label">Téléphone</label>
              <input
                id="phone"
                v-model="form.phone"
                class="field-input"
                type="tel"
                placeholder="06 12 34 56 78"
              />
            </div>
          </div>
        </div>

        <!-- Event info section -->
        <div>
          <h2
            style="
              font-family: var(--mono);
              font-size: 10px;
              letter-spacing: 0.18em;
              text-transform: uppercase;
              color: var(--ink-dim);
              margin-bottom: 20px;
              padding-bottom: 8px;
              border-bottom: 1px solid var(--line);
            "
          >
            Informations sur l'événement
          </h2>
          <div style="display: flex; flex-direction: column; gap: 24px">
            <div>
              <label for="name" class="field-label">Nom de l'événement *</label>
              <input
                id="name"
                v-model="form.name"
                class="field-input"
                :class="{ invalid: errors.name }"
                type="text"
                placeholder="Ex: CrossFit Open 2026"
              />
              <span v-if="errors.name" class="field-error">{{
                errors.name
              }}</span>
            </div>

            <div>
              <label for="eventLink" class="field-label"
                >Lien vers l'événement *</label
              >
              <input
                id="eventLink"
                v-model="form.eventLink"
                class="field-input"
                :class="{ invalid: errors.eventLink }"
                type="url"
                placeholder="https://..."
              />
              <div v-if="isAutoAddedSource" class="field-warning">
                ⚠️ Les événements provenant de Competition Corner, scoring.fit
                et Hyrox sont automatiquement ajoutés à notre base de données.
                Votre suggestion n'est probablement pas nécessaire.
              </div>
              <span v-if="errors.eventLink" class="field-error">{{
                errors.eventLink
              }}</span>
            </div>

            <div>
              <label class="field-label">Type *</label>
              <div style="display: flex; gap: 8px; margin-top: 4px">
                <button
                  type="button"
                  class="chip"
                  :class="{ 'chip-active': form.type === 'onSite' }"
                  @click="form.type = 'onSite'"
                >
                  ○ Sur place
                </button>
                <button
                  type="button"
                  class="chip"
                  :class="{ 'chip-active': form.type === 'online' }"
                  @click="form.type = 'online'"
                >
                  ● En ligne
                </button>
              </div>
              <span v-if="errors.type" class="field-error">{{
                errors.type
              }}</span>
            </div>

            <div v-if="form.type === 'onSite'">
              <label class="field-label">Adresse *</label>
              <AddressAutocomplete
                v-model="form.location"
                v-model:confirmed="locationConfirmed"
                placeholder="Ex: 123 Rue de la Paix, 75001 Paris"
                :has-error="!!errors.location"
              />
              <span v-if="errors.location" class="field-error">{{
                errors.location
              }}</span>
              <span class="field-hint"
                >L'adresse complète permet de calculer automatiquement le
                département</span
              >
            </div>

            <div
              style="
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 24px 32px;
              "
            >
              <div>
                <label class="field-label">Date de début *</label>
                <DatePicker
                  v-model="form.startDate"
                  :min="today"
                  placeholder="jj/mm/aaaa"
                  :class="{ invalid: errors.startDate }"
                />
                <span v-if="errors.startDate" class="field-error">{{
                  errors.startDate
                }}</span>
              </div>
              <div>
                <label class="field-label">Date de fin *</label>
                <DatePicker
                  v-model="form.endDate"
                  :min="form.startDate || today"
                  placeholder="jj/mm/aaaa"
                  :class="{ invalid: errors.endDate }"
                />
                <span v-if="errors.endDate" class="field-error">{{
                  errors.endDate
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Submit -->
        <div style="display: flex; gap: 12px; padding-top: 8px">
          <button
            type="submit"
            class="btn btn-primary"
            style="flex: 1"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? 'Envoi…' : 'Envoyer →' }}
          </button>
          <button type="button" class="btn" @click="navigateTo('/')">
            Annuler
          </button>
        </div>
      </form>
    </template>
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import type { EventType } from '@/types/Event';
import { API_ENDPOINTS } from '@/config/api';

definePageMeta({ layout: 'search' });

useSeoMeta({
  title: 'Suggérer un événement | Crossfit Event Scanner',
  description: 'Proposez un événement CrossFit à ajouter au calendrier',
});

const { fetchWithAuth } = useApiConfig();
const { add: notify } = useNotification();

interface EventForm {
  name: string;
  eventLink: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  type: Exclude<EventType, 'ALL'> | null;
  startDate: Date | null;
  endDate: Date | null;
  location: string;
}

const form = ref<EventForm>({
  name: '',
  eventLink: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  type: null,
  startDate: null,
  endDate: null,
  location: '',
});

const locationConfirmed = ref(false);

const errors = ref<Partial<Record<keyof EventForm, string>>>({});
const isSubmitting = ref(false);
const submitted = ref(false);

const today = dayjs().startOf('day').toDate();

const AUTO_ADDED_DOMAINS = [
  'competitioncorner.net',
  'scoring.fit',
  'hyrox.com',
  'maboxdecross.fr',
];

const isAutoAddedSource = computed(() =>
  AUTO_ADDED_DOMAINS.some((domain) => form.value.eventLink.includes(domain)),
);

watch(
  () => form.value.type,
  (newType) => {
    if (newType === 'online') {
      form.value.location = '';
      locationConfirmed.value = false;
      errors.value.location = undefined;
    }
  },
);

function validateForm(): boolean {
  errors.value = {};
  let isValid = true;

  if (!form.value.name.trim()) {
    errors.value.name = 'Le nom est requis';
    isValid = false;
  }
  if (!form.value.eventLink.trim()) {
    errors.value.eventLink = 'Le lien est requis';
    isValid = false;
  } else if (
    !form.value.eventLink.startsWith('http://') &&
    !form.value.eventLink.startsWith('https://')
  ) {
    errors.value.eventLink = 'Le lien doit commencer par http:// ou https://';
    isValid = false;
  } else if (isAutoAddedSource.value) {
    errors.value.eventLink =
      'Cet événement est automatiquement ajouté depuis cette source. Aucune suggestion nécessaire.';
    isValid = false;
  }
  if (!form.value.firstName.trim()) {
    errors.value.firstName = 'Le prénom est requis';
    isValid = false;
  }
  if (!form.value.lastName.trim()) {
    errors.value.lastName = 'Le nom est requis';
    isValid = false;
  }
  if (!form.value.email.trim()) {
    errors.value.email = "L'email est requis";
    isValid = false;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
    errors.value.email = "L'email n'est pas valide";
    isValid = false;
  }
  if (!form.value.type) {
    errors.value.type = 'Le type est requis';
    isValid = false;
  }
  if (!form.value.startDate) {
    errors.value.startDate = 'La date de début est requise';
    isValid = false;
  } else if (dayjs(form.value.startDate).isBefore(dayjs(), 'day')) {
    errors.value.startDate = 'La date de début ne peut pas être dans le passé';
    isValid = false;
  }
  if (!form.value.endDate) {
    errors.value.endDate = 'La date de fin est requise';
    isValid = false;
  }
  if (
    form.value.startDate &&
    form.value.endDate &&
    form.value.startDate > form.value.endDate
  ) {
    errors.value.endDate = 'La date de fin doit être après la date de début';
    isValid = false;
  }
  if (form.value.type === 'onSite' && !form.value.location.trim()) {
    errors.value.location =
      "L'adresse est requise pour les événements sur place";
    isValid = false;
  } else if (form.value.type === 'onSite' && !locationConfirmed.value) {
    errors.value.location =
      'Veuillez sélectionner une adresse depuis les suggestions';
    isValid = false;
  }
  return isValid;
}

async function handleSubmit() {
  if (!validateForm()) {
    notify('Veuillez corriger les erreurs dans le formulaire', 'error');
    return;
  }
  try {
    isSubmitting.value = true;
    await fetchWithAuth(API_ENDPOINTS.SUGGEST_EVENT, {
      method: 'POST',
      body: {
        name: form.value.name,
        eventLink: form.value.eventLink,
        firstName: form.value.firstName,
        lastName: form.value.lastName,
        email: form.value.email,
        phone: form.value.phone || null,
        type: form.value.type,
        startDate: form.value.startDate,
        endDate: form.value.endDate,
        location: form.value.location || null,
      },
    });
    submitted.value = true;
  } catch (error) {
    notify(extractApiErrorMessage(error), 'error');
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<style scoped>
.field-warning {
  margin-top: 6px;
  padding: 8px 12px;
  border-radius: 6px;
  background-color: #fef9c3;
  border: 1px solid #fde047;
  color: #713f12;
  font-size: 13px;
  line-height: 1.4;
}

.dark .field-warning {
  background-color: #422006;
  border-color: #a16207;
  color: #fef08a;
}
</style>
